import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  Badge
} from "reactstrap";
import { 
  FaEdit, 
  FaTrash, 
  FaSave, 
  FaTimes, 
  FaFileAlt,
  FaCalendarAlt,
  FaInfoCircle
} from "react-icons/fa";

const API_BASE_URL = "https://api.editezy.com/api/admin";

const PrivacyPolicyPage = () => {
  const [privacyPolicy, setPrivacyPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [apiMessage, setApiMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [editMode, setEditMode] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [date, setDate] = useState("");

  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/getpolicy`);
        
        console.log("API Response:", response.data);

        // Check if response has _id (actual policy data)
        if (response.data && response.data._id) {
          // We have actual policy data
          setPrivacyPolicy(response.data);
          setTitle(response.data.title || "");
          setContent(response.data.content || "");
          setDate(response.data.date ? new Date(response.data.date).toISOString().slice(0, 10) : "");
          setApiMessage("");
        } 
        // Check if response has message (like "Privacy policy not found.")
        else if (response.data && response.data.message) {
          setApiMessage(response.data.message);
          setMessageType("info");
          setPrivacyPolicy(null);
        } 
        // Fallback
        else {
          setApiMessage("No data available");
          setMessageType("info");
          setPrivacyPolicy(null);
        }
      } catch (err) {
        console.error("Error fetching privacy policy:", err);
        
        // Check if error response has message
        if (err.response && err.response.data && err.response.data.message) {
          setApiMessage(err.response.data.message);
          setMessageType("error");
          setPrivacyPolicy(null);
        } else if (err.response) {
          if (err.response.status === 404) {
            setApiMessage("Privacy policy endpoint not found");
          } else {
            setApiMessage(`Server error: ${err.response.status}`);
          }
          setMessageType("error");
        } else if (err.request) {
          setApiMessage("Network error - please check your connection");
          setMessageType("error");
        } else {
          setApiMessage("An unexpected error occurred");
          setMessageType("error");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPrivacyPolicy();
  }, []);

  const handleUpdate = async () => {
    if (!title.trim() || !content.trim() || !date) {
      alert("Please fill in all fields before updating.");
      return;
    }
    
    setSubmitting(true);
    setApiMessage("");
    
    try {
      const response = await axios.put(
        `${API_BASE_URL}/updatepolicy/${privacyPolicy._id}`,
        { title, content, date }
      );
      
      // Show success message
      setApiMessage("Privacy policy updated successfully!");
      setMessageType("success");
      
      // Update local state with response data
      if (response.data && response.data._id) {
        setPrivacyPolicy(response.data);
        setTitle(response.data.title || "");
        setContent(response.data.content || "");
        setDate(response.data.date ? new Date(response.data.date).toISOString().slice(0, 10) : "");
      } else if (response.data && response.data.policy) {
        setPrivacyPolicy(response.data.policy);
        setTitle(response.data.policy.title || "");
        setContent(response.data.policy.content || "");
        setDate(response.data.policy.date ? new Date(response.data.policy.date).toISOString().slice(0, 10) : "");
      }
      
      setEditMode(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setApiMessage("");
      }, 3000);
      
    } catch (err) {
      console.error("Error updating privacy policy:", err);
      
      if (err.response && err.response.data && err.response.data.message) {
        setApiMessage(err.response.data.message);
        setMessageType("error");
      } else {
        setApiMessage("Failed to update privacy policy.");
        setMessageType("error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete the privacy policy? This action cannot be undone.")) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.delete(
        `${API_BASE_URL}/deletepolicy/${privacyPolicy._id}`
      );
      
      // Show success message
      setApiMessage("Privacy policy deleted successfully!");
      setMessageType("success");
      
      // Clear local state
      setPrivacyPolicy(null);
      setTitle("");
      setContent("");
      setDate("");
      setEditMode(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setApiMessage("");
      }, 3000);
      
    } catch (err) {
      console.error("Error deleting privacy policy:", err);
      
      if (err.response && err.response.data && err.response.data.message) {
        setApiMessage(err.response.data.message);
        setMessageType("error");
      } else {
        setApiMessage("Failed to delete privacy policy.");
        setMessageType("error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setApiMessage("");
    
    if (privacyPolicy && privacyPolicy._id) {
      setTitle(privacyPolicy.title || "");
      setContent(privacyPolicy.content || "");
      setDate(privacyPolicy.date ? new Date(privacyPolicy.date).toISOString().slice(0, 10) : "");
    } else {
      setTitle("");
      setContent("");
      setDate("");
    }
  };

  // Loading State
  if (loading) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card className="shadow-sm border-0">
              <CardBody className="text-center py-5">
                <Spinner color="primary" style={{ width: '3rem', height: '3rem' }}>
                  Loading...
                </Spinner>
                <p className="mt-3 text-muted">Loading privacy policy...</p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="shadow-lg border-0">
            <CardHeader 
              className="py-3 d-flex align-items-center justify-content-between"
              style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                borderBottom: 'none'
              }}
            >
              <div className="d-flex align-items-center">
                <FaFileAlt className="me-2" size={24} />
                <h4 className="mb-0">Privacy Policy Management</h4>
              </div>
              {!editMode && privacyPolicy && privacyPolicy._id && (
                <Badge color="light" pill className="px-3 py-2">
                  Active
                </Badge>
              )}
              {editMode && (
                <Badge color="warning" pill className="px-3 py-2">
                  Editing
                </Badge>
              )}
            </CardHeader>
            
            <CardBody className="p-4">
              {/* API Message Display */}
              {apiMessage && (
                <Alert 
                  color={
                    messageType === 'success' ? 'success' : 
                    messageType === 'error' ? 'danger' : 
                    'info'
                  }
                  className="d-flex align-items-center border-0 shadow-sm mb-4"
                >
                  <FaInfoCircle className={`me-3 ${
                    messageType === 'success' ? 'text-success' : 
                    messageType === 'error' ? 'text-danger' : 
                    'text-primary'
                  }`} size={20} />
                  <div>
                    <strong className="d-block mb-1">
                      {messageType === 'success' ? 'Success' : 
                       messageType === 'error' ? 'Error' : 
                       'Information'}
                    </strong>
                    <p className="mb-0">{apiMessage}</p>
                  </div>
                </Alert>
              )}

              {/* No Policy Found State */}
              {!privacyPolicy && !editMode && (
                <div className="text-center py-5">
                  <div className="mb-4">
                    <FaFileAlt size={64} className="text-muted" style={{ opacity: 0.5 }} />
                  </div>
                  <h5 className="text-muted mb-3">No Privacy Policy Found</h5>
                  <p className="text-muted mb-0">
                    {apiMessage || "There is no privacy policy available."}
                  </p>
                </div>
              )}

              {/* Edit Mode Form */}
              {editMode && privacyPolicy && (
                <Form>
                  <FormGroup>
                    <Label for="title" className="fw-bold">
                      Policy Title <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      id="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter policy title (e.g., Privacy Policy)"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label for="content" className="fw-bold">
                      Policy Content <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="textarea"
                      id="content"
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Enter policy content here..."
                      required
                      style={{ minHeight: '200px' }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label for="date" className="fw-bold">
                      Effective Date <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="date"
                      id="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      required
                    />
                  </FormGroup>

                  <div className="d-flex gap-2 mt-4">
                    <Button
                      color="primary"
                      onClick={handleUpdate}
                      disabled={submitting || !title.trim() || !content.trim() || !date}
                      className="px-4"
                    >
                      {submitting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <FaSave className="me-2" />
                          Update Policy
                        </>
                      )}
                    </Button>
                    
                    <Button
                      color="secondary"
                      onClick={handleCancel}
                      disabled={submitting}
                    >
                      <FaTimes className="me-2" />
                      Cancel
                    </Button>
                  </div>
                </Form>
              )}

              {/* View Mode - Shows when policy exists */}
              {privacyPolicy && privacyPolicy._id && !editMode && (
                <>
                  <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                      <h2 className="text-primary mb-2">{privacyPolicy.title}</h2>
                      <div className="d-flex align-items-center text-muted">
                        <FaCalendarAlt className="me-2" size={14} />
                        <small>
                          Effective Date: {new Date(privacyPolicy.date).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </small>
                      </div>
                      <div className="d-flex align-items-center text-muted mt-1">
                        <small>
                          Last Updated: {new Date(privacyPolicy.updatedAt).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </small>
                      </div>
                    </div>
                    <Badge color="success" pill className="px-3 py-2">
                      Active
                    </Badge>
                  </div>

                  <div className="border rounded p-4 bg-light mb-4">
                    <div className="text-gray-700 leading-relaxed" style={{ whiteSpace: 'pre-line' }}>
                      {privacyPolicy.content}
                    </div>
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      color="primary"
                      onClick={() => setEditMode(true)}
                      className="px-4"
                      disabled={submitting}
                    >
                      <FaEdit className="me-2" />
                      Edit Policy
                    </Button>
                    
                    <Button
                      color="danger"
                      onClick={handleDelete}
                      disabled={submitting}
                      className="px-4"
                    >
                      {submitting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <FaTrash className="me-2" />
                          Delete Policy
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PrivacyPolicyPage;